import React, { useEffect, useMemo, useState } from "react";
import { faTruck, faTrash, faMapMarkerAlt } from "@fortawesome/free-solid-svg-icons";
import { getAssetCategorySummary } from "../../../Apis/DashboardApis/primaryDashboardApi";
import CardLoader from "../../../Common/ShimmerLoader/CardLoader";
import DashboardSectionCard from "./shared/DashboardSectionCard";
import DashboardFilters from "./shared/DashboardFilters";
import PlannedActualCard from "./shared/PlannedActualCard";
import useDashboardFilters from "./shared/useDashboardFilters";

const CATEGORY_CONFIG = {
  dhaloas: { icon: faTruck, color: "#12939a", className: "pd-waste-dhalaos" },
  dhalaos: { icon: faTruck, color: "#12939a", className: "pd-waste-dhalaos" },
  bins: { icon: faTrash, color: "#0056d2", className: "pd-waste-bins" },
  "open site": { icon: faMapMarkerAlt, color: "#7b2cbf", className: "pd-waste-open-site" },
};

const DEFAULT_CONFIG = { icon: faTruck, color: "#2563eb", className: "" };

const formatCount = (value) => {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  return Number(value).toLocaleString();
};

const buildCards = (categories) => {
  const list = Array.isArray(categories) ? categories : [];

  return list.map((item) => {
    const key = (item?.categoryName || "").toLowerCase();
    const config = CATEGORY_CONFIG[key] || DEFAULT_CONFIG;

    return {
      icon: config.icon,
      title: item?.categoryName || "Category",
      planned: formatCount(item?.plannedCount),
      actual: formatCount(item?.actualCount),
      color: config.color,
      className: config.className,
    };
  });
};

const WasteCollectionSummary = ({ wards, data }) => {
  const {
    filters,
    appliedFilters,
    handleFilterChange,
    handleSearch,
  } = useDashboardFilters();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(!data);

  useEffect(() => {
    if (Array.isArray(data)) {
      return undefined;
    }

    let isMounted = true;

    const fetchSummary = async () => {
      try {
        setLoading(true);
        const response = await getAssetCategorySummary(appliedFilters);

        if (isMounted && response?.status) {
          setSummary(response?.data ?? null);
        }
      } catch (error) {
        console.error("Failed to load waste collection summary:", error);
        if (isMounted) {
          setSummary(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchSummary();

    return () => {
      isMounted = false;
    };
  }, [appliedFilters, data]);

  const cards = useMemo(() => {
    if (Array.isArray(data)) {
      return data;
    }

    return buildCards(summary?.categories);
  }, [data, summary]);

  return (
    <DashboardSectionCard
      title="Waste Collection Summary"
      className="pd-waste-section"
      filters={
        <DashboardFilters
          className="pd-waste-filters"
          searchVariant="inline"
          fromDate={filters?.fromDate}
          toDate={filters?.toDate}
          ward={filters?.ward}
          wards={wards}
          onFromDateChange={(value) => handleFilterChange("fromDate", value)}
          onToDateChange={(value) => handleFilterChange("toDate", value)}
          onWardChange={(value) => handleFilterChange("ward", value)}
          onSearch={handleSearch}
          showSearch
        />
      }
    >
      {loading ? (
        <CardLoader count={3} />
      ) : (
        <div className="pd-metric-row cols-3 pd-waste-row">
          {cards.map((item) => (
            <PlannedActualCard key={item.title} {...item} />
          ))}
        </div>
      )}
    </DashboardSectionCard>
  );
};

export default WasteCollectionSummary;
